import { Injectable } from '@angular/core';
import { CoreRepository } from '@tcc-mono/shared/data-access';
import { Animal, AnimalSelection, SelectionMarkColor } from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionAddAnimal, AnimalSelectionAddAnimalResponse, AnimalSelectionService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { EMPTY, Observable, map, of, shareReplay, switchMap, take, tap } from 'rxjs';

type AnimalsPaginationState = {
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  allPagesLoaded: boolean;
};

const ANIMALS_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root'
})
export class AnimalSelectionRepository extends CoreRepository<AnimalSelection> {
  private readonly _selectionMarkColorsCache = new Map<number, Observable<SelectionMarkColor[]>>();
  private readonly _animalsPagination = new Map<number, AnimalsPaginationState>();
  private readonly _sortedAnimalsCache = new Map<number, { signature: string; sourceRef: Animal[]; animals: Animal[] }>();

  public getAnimalsPagination = (selectionId: number): AnimalsPaginationState | null =>
    this._animalsPagination.get(selectionId) ?? null;

  public hasMoreSelectionAnimals = (selectionId: number): boolean => {
    const pag = this._animalsPagination.get(selectionId);
    return !!pag && !pag.allPagesLoaded && pag.currentPage < pag.lastPage;
  };

  /** Dieren al eens geladen voor deze selectie (store)? */
  public hasCachedSelectionAnimals = (entityId: number): boolean =>
    !!entityId && this.entityHasProperty(entityId, 'animals');

  /** Gesorteerde dieren (pivot created_at desc = meest recent toegevoegd bovenaan). */
  public getSortedSelectionAnimals = (animals: Animal[] | undefined, selectionId: number): Animal[] => {
    if (!animals?.length) {
      return animals ?? [];
    }

    const signature = this._animalsSortSignature(animals);
    const cached = this._sortedAnimalsCache.get(selectionId);
    if (cached?.signature === signature && cached.sourceRef === animals) {
      return cached.animals;
    }

    const sorted = [...animals].sort((a, b) => this._compareSelectionAnimalsByAddedAt(a, b));
    this._sortedAnimalsCache.set(selectionId, { signature, sourceRef: animals, animals: sorted });
    return sorted;
  };

  private _compareSelectionAnimalsByAddedAt = (a: Animal, b: Animal): number => {
    const byAddedAt = (b.created_at || '').localeCompare(a.created_at || '');
    if (byAddedAt !== 0) {
      return byAddedAt;
    }
    return (b.id ?? 0) - (a.id ?? 0);
  };

  private _animalsSortSignature = (animals: Animal[]): string => {
    if (!animals.length) {
      return '0';
    }

    let maxCreated = '';
    for (const animal of animals) {
      const created = animal.created_at || '';
      if (created > maxCreated) {
        maxCreated = created;
      }
    }

    const firstId = animals[0]?.id ?? 0;
    const lastId = animals[animals.length - 1]?.id ?? 0;
    return `${animals.length}:${firstId}:${lastId}:${maxCreated}`;
  };

  private _invalidateSortedAnimalsCache = (selectionId: number): void => {
    this._sortedAnimalsCache.delete(selectionId);
  };

  private _syncAnimalsPaginationTotal = (selectionId: number, total: number): void => {
    const pag = this._animalsPagination.get(selectionId);
    if (!pag) {
      return;
    }

    this._animalsPagination.set(selectionId, { ...pag, total });
  };

  /** Zorg dat created_at pivot-timestamp is (fallback voor optimistic UI). */
  private _normalizeAddedSelectionAnimal = (animal: Animal): Animal => ({
    ...animal,
    created_at: animal.created_at ?? new Date().toISOString()
  });

  /** Detail + dieren: standaard alleen pagina 1 (snel). Gebruik allPages voor volledige lijst. */
  public getSelectionWithAnimals = (
    entityId: number,
    setActive = true,
    forceGet = false,
    allPages = false
  ): Observable<AnimalSelection> => {
    if (!entityId) {
      return EMPTY;
    }

    if (setActive) {
      this.setActiveEntity(entityId);
    }

    if (forceGet) {
      this._animalsPagination.delete(entityId);
      this._invalidateSortedAnimalsCache(entityId);
    }

    const needsFetch =
      forceGet ||
      !this.hasEntity(entityId) ||
      !this.entityHasProperty(entityId, 'animals');

    if (!needsFetch) {
      return this.selectEntity(entityId);
    }

    return this._animalSelectionService.get(entityId).pipe(
      switchMap(({ data: selection }) =>
        (allPages
          ? this._animalSelectionService.getAllAnimals(entityId).pipe(
              map((animals) => {
                this._animalsPagination.set(entityId, {
                  currentPage: 1,
                  lastPage: 1,
                  total: animals.length,
                  perPage: animals.length || ANIMALS_PAGE_SIZE,
                  allPagesLoaded: true
                });
                return { ...selection, animals };
              })
            )
          : this._loadAnimalsFirstPage(entityId, selection)
        )
      ),
      tap((selection) => {
        this._invalidateSortedAnimalsCache(entityId);
        this.addEntitiesInStore([selection]);
      })
    );
  };

  /** Volgende pagina dieren laden en appenden in store. */
  public loadMoreSelectionAnimals = (entityId: number): Observable<AnimalSelection | null> => {
    const pag = this._animalsPagination.get(entityId);
    if (!pag || pag.allPagesLoaded || pag.currentPage >= pag.lastPage) {
      return of(null);
    }

    const nextPage = pag.currentPage + 1;

    return this._animalSelectionService.getAnimalsPage(entityId, nextPage, pag.perPage).pipe(
      switchMap((response) =>
        this.selectEntity(entityId).pipe(
          take(1),
          map((current) => {
            const pageAnimals = response.data ?? [];
            const animals = [...(current?.animals ?? []), ...pageAnimals];
            const lastPage = response.meta?.last_page ?? pag.lastPage;
            const currentPage = response.meta?.current_page ?? nextPage;

            this._animalsPagination.set(entityId, {
              currentPage,
              lastPage,
              total: response.meta?.total ?? pag.total,
              perPage: pag.perPage,
              allPagesLoaded: currentPage >= lastPage
            });

            return { ...current, animals, animal_count: response.meta?.total ?? current?.animal_count };
          })
        )
      ),
      tap((selection) => {
        if (selection) {
          this._invalidateSortedAnimalsCache(entityId);
          this.addEntitiesInStore([selection]);
        }
      })
    );
  };

  private _loadAnimalsFirstPage = (
    entityId: number,
    selection: AnimalSelection
  ): Observable<AnimalSelection> =>
    this._animalSelectionService.getAnimalsPage(entityId, 1, ANIMALS_PAGE_SIZE).pipe(
      map((response) => {
        const animals = response.data ?? [];
        const lastPage = response.meta?.last_page ?? 1;
        this._animalsPagination.set(entityId, {
          currentPage: response.meta?.current_page ?? 1,
          lastPage,
          total: response.meta?.total ?? animals.length,
          perPage: ANIMALS_PAGE_SIZE,
          allPagesLoaded: lastPage <= 1
        });
        return {
          ...selection,
          animals,
          animal_count: response.meta?.total ?? selection.animal_count ?? animals.length
        };
      })
    );

  public constructor(
    protected readonly _animalSelectionService: AnimalSelectionService
  ) {
    super(
      { name: 'animalSelections' },
      _animalSelectionService
    )
  }

  public addAnimal = (selectionId: number, data: AnimalSelectionAddAnimal): Observable<AnimalSelectionAddAnimalResponse> => {
    return this._animalSelectionService.postAnimal(selectionId, data).pipe(
      switchMap((response) =>
        this.selectEntity(selectionId).pipe(
          take(1),
          tap((current) => this._mergeAnimalAdded(current, response)),
          map(() => response)
        )
      )
    );
  }

  public removeAnimal = (selectionId: number, animalId: number): Observable<unknown> => {
    return this._animalSelectionService.removeAnimal(selectionId, animalId).pipe(
      switchMap((result) =>
        this.selectEntity(selectionId).pipe(
          take(1),
          tap((current) => this._mergeAnimalRemoved(current, animalId)),
          map(() => result)
        )
      )
    );
  }

  /** Filterkleuren met min/max voor deze selectie (gecached per selectionId). */
  public getSelectionMarkColorsForSelection = (selectionId: number): Observable<SelectionMarkColor[]> => {
    if (!selectionId) {
      return of([]);
    }

    if (!this._selectionMarkColorsCache.has(selectionId)) {
      const request$ = this._animalSelectionService.getSelectionMarkColorsForSelection(selectionId).pipe(
        shareReplay(1)
      );
      this._selectionMarkColorsCache.set(selectionId, request$);
    }

    return this._selectionMarkColorsCache.get(selectionId)!;
  }

  /** Filterinstellingen (min/max per kleur) voor deze selectie opslaan. */
  public updateSelectionMarkColors = (
    selectionId: number,
    body: { selection_mark_colors: Array<{ color_name: string; used?: boolean; min_birth_date?: string | null; max_birth_date?: string | null }> }
  ): Observable<SelectionMarkColor[]> => {
    return this._animalSelectionService.updateSelectionMarkColors(selectionId, body).pipe(
      tap((colors) => {
        this._selectionMarkColorsCache.set(selectionId, of(colors).pipe(shareReplay(1)));
      })
    );
  }

  public updateAnimalMarkColor = (
    selectionId: number,
    animalId: number,
    selection_mark_color: string | null
  ): Observable<unknown> => {
    return this._animalSelectionService.updateAnimalMarkColor(selectionId, animalId, selection_mark_color).pipe(
      switchMap((result) =>
        this.selectEntity(selectionId).pipe(
          take(1),
          tap((current) => this._mergeAnimalMarkColorUpdated(current, animalId, selection_mark_color)),
          map(() => result)
        )
      )
    );
  }

  private _mergeAnimalAdded(current: AnimalSelection | undefined, response: AnimalSelectionAddAnimalResponse): void {
    if (!current || !response.animal) {
      return;
    }

    const animals = current.animals ?? [];
    const addedAnimal = this._normalizeAddedSelectionAnimal(response.animal);
    const exists = animals.some((a) => a.id === addedAnimal.id);
    const updatedAnimals = exists
      ? animals.map((a) => (a.id === addedAnimal.id ? addedAnimal : a))
      : [addedAnimal, ...animals];

    const pag = this._animalsPagination.get(current.id);
    const animal_count = response.selection?.animal_count ?? (
      exists
        ? (current.animal_count ?? animals.length)
        : (current.animal_count ?? pag?.total ?? animals.length) + 1
    );

    this._invalidateSortedAnimalsCache(current.id);
    this._syncAnimalsPaginationTotal(current.id, animal_count);
    this.addEntitiesInStore([{
      ...current,
      ...(response.selection ?? {}),
      animals: updatedAnimals,
      animal_count
    }]);
  }

  private _mergeAnimalRemoved(current: AnimalSelection | undefined, animalId: number): void {
    if (!current) {
      return;
    }

    const animals = (current.animals ?? []).filter((a) => a.id !== animalId);
    const pag = this._animalsPagination.get(current.id);
    const animal_count = Math.max(
      0,
      (current.animal_count ?? pag?.total ?? animals.length + 1) - 1
    );

    this._invalidateSortedAnimalsCache(current.id);
    this._syncAnimalsPaginationTotal(current.id, animal_count);
    this.addEntitiesInStore([{
      ...current,
      animals,
      animal_count
    }]);
  }

  private _mergeAnimalMarkColorUpdated(
    current: AnimalSelection | undefined,
    animalId: number,
    selection_mark_color: string | null
  ): void {
    if (!current?.animals) {
      return;
    }

    const animals = current.animals.map((a) =>
      a.id === animalId
        ? {
            ...a,
            selection_mark_color,
            selection_mark_color_name: selection_mark_color
              ? selection_mark_color.charAt(0).toUpperCase() + selection_mark_color.slice(1)
              : null
          }
        : a
    );

    this._invalidateSortedAnimalsCache(current.id);
    this.addEntitiesInStore([{ ...current, animals }]);
  }

  public mergeSelections = (selectionIds: number[]): Observable<unknown> => {
    return this._animalSelectionService.mergeSelections(selectionIds);
  }

  public createPassports = (selectionId: number): Observable<unknown> => {
    return this._animalSelectionService.createPassports(selectionId);
  }

  public export = (selectionId: number, body: { date: string }): Observable<unknown> => {
    return this._animalSelectionService.export(selectionId, body);
  }

  public exportFromHub = (selectionId: number, body: { date: string, hub_id: number }): Observable<unknown> => {
    return this._animalSelectionService.exportFromHub(selectionId, body);
  }

  public transport = (selectionId: number, body: { date: string, stable_id: number }): Observable<unknown> => {
    return this._animalSelectionService.transport(selectionId, body);
  }
}
